import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminUser, setAdminUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Safe localStorage getter
  const getLocalStorageItem = (key) => {
    try {
      const item = localStorage.getItem(key);
      return item && item !== 'undefined' ? item : null;
    } catch (error) {
      console.error(`Error reading ${key} from localStorage:`, error);
      return null;
    }
  };

  // Safe localStorage setter
  const setLocalStorageItem = (key, value) => {
    try {
      if (value === null || value === undefined) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, value);
      }
    } catch (error) {
      console.error(`Error setting ${key} in localStorage:`, error);
    }
  };

  useEffect(() => {
    const token = getLocalStorageItem('adminToken');
    const user = getLocalStorageItem('adminUser');

    if (token && user) {
      try {
        const userData = JSON.parse(user);
        setIsAuthenticated(true);
        setAdminUser(userData);
      } catch (error) {
        console.error('Error parsing user data from localStorage:', error);
        // Clear invalid data
        setLocalStorageItem('adminToken', null);
        setLocalStorageItem('adminUser', null);
      }
    }
    setLoading(false);
  }, []);

  const verifyToken = async () => {
    try {
      // You might want to add a token verification endpoint
      // For now, we'll just set loading to false
      setLoading(false);
    } catch (error) {
      console.error('Token verification failed:', error);
      logout();
    }
  };

  const login = async (credentials) => {
    try {
      // console.log('Attempting login with credentials:', credentials);
      
      const response = await authAPI.login(credentials);
      // console.log('Login response:', response.data);
      
      const { token, user } = response.data.data || response.data;
      
      if (!token) {
        throw new Error('No token received from server');
      }

      setLocalStorageItem('adminToken', token);
      setLocalStorageItem('adminUser', JSON.stringify(user));
      
      setIsAuthenticated(true);
      setAdminUser(user);
      
      return { success: true, user };
    } catch (error) {
      console.error('Login error:', error);
      
      let errorMessage = 'Login failed';
      
      if (error.response?.data) {
        // Handle HTML error response
        if (typeof error.response.data === 'string' && error.response.data.includes('<!DOCTYPE html>')) {
          errorMessage = 'Server error: Received HTML instead of JSON response';
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return { 
        success: false, 
        error: errorMessage 
      };
    }
  };

  const logout = () => {
    setLocalStorageItem('adminToken', null);
    setLocalStorageItem('adminUser', null);
    setIsAuthenticated(false);
    setAdminUser(null);
  };

  const value = {
    isAuthenticated,
    adminUser,
    loading,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};